import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  Observable,
  Subject,
  Subscription,
  takeUntil,
  take,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  of,
  map,
  filter,
  merge,
  catchError,
  finalize,
} from 'rxjs';
import * as TankProfileActions from '@state/actions/tank-profile.actions';
import { AppState } from '../../app.state';
import { ITankGridColumn } from '../../interfaces/tankGridColumn';
import { ITankProfile } from '@interfaces/tankProfile';
import { LogService } from '@services/log.service';
import { TranslateService } from '@ngx-translate/core';
import { SettingsDialogService } from '@components/settings/services/settings-dialog.service';
import { selectLastSelectedTankProfile } from '@state/selectors/tank-profile.selectors';
import { TankProfileService } from '@services/tank-profile.service';
import { AppDialogService } from '@services/app-dialog.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ValidationState } from '@utils/validators';
import { cloneDeep } from 'lodash-es';
import { PRE_TELEMETRY_FALLBACK_TEXT, PRE_TELEMETRY_RESOURCE_KEY } from '../../shared/Constants/sharedConstants';
export interface TankProfileColumn {
  columnId: number;
  name: string;
  selected: boolean;
}


@Component({
  selector: 'tank-profile-settings',
  templateUrl: './tank-profile-settings.component.html',
  styleUrls: ['./tank-profile-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** TankProfileSettings component*/
export class TankProfileSettingsComponent implements OnInit, OnDestroy {
  private readonly preTelemetryFallbackText = PRE_TELEMETRY_FALLBACK_TEXT;
  private readonly preTelemetryInfoColumns = new Set([
    'sixtydayavgconvertedvalue',
    'ninetydayavgconvertedvalue',
    'avgdailyusage60',
    'avgdailyusage90',
  ]);
  destroy$: Subject<boolean> = new Subject<boolean>();
  lastSelectedProfile$ = this.store
    .select(selectLastSelectedTankProfile)
    .pipe(takeUntil(this.destroy$));

  private _currentTankProfile$: Observable<ITankProfile>;
  private lastSelectedProfileSub: Subscription;
  loadingProfile: boolean;
  inPreview: boolean = false;
  preTelemetryInfoText: string;

  public get currentTankProfile$(): Observable<ITankProfile> {
    return this._currentTankProfile$;
  }
  @Input()
  public set currentTankProfile$(value: Observable<ITankProfile>) {
    this._currentTankProfile$ = value;
    if (!this._currentTankProfile$) {
      if (this.currentTankSub) this.currentTankSub.unsubscribe();
      return;
    }
    this.setupTankProfileSubscription();
  }

  public get isDirty(): boolean {
    return this.tankProfileForm.dirty || this.isAnyValidationChecking;
  }

  readonly PRIMARY_SORT_ASCENDING = 2;
  readonly PRIMARY_SORT_DESCENDING = 3;
  readonly SECONDARY_SORT_ASCENDING = 4;
  readonly SECONDARY_SORT_DESCENDING = 5;
  readonly NO_SORT_COLUMN = -1;

  @Output()
  tankProfileSaved = new EventEmitter<ITankProfile>();

  lastSelectedProfileId: number;
  readonly pageLengths: number[] = [10, 25, 50, 100, 250, 500];
  //the standard lengths plus the profile's own saved value when it is not one of them, so it is never silently reset
  pageLengthOptions: number[] = this.pageLengths;

  columnFilter = '';
  visibleColumns: ITankGridColumn[] = [];
  //sort pickers list columns alphabetically - the grid's column order means nothing when choosing a sort
  sortSearch = { primary: '', secondary: '' };
  sortOptions: { primary: ITankGridColumn[]; secondary: ITankGridColumn[] } = { primary: [], secondary: [] };
  private columnsByLabel: ITankGridColumn[] = [];
  //the last name the duplicate check finished for, so a rename cannot be confirmed inside the debounce window
  lastCheckedName: string = null;
  private columnLabels = new Map<number, string>();
  primaryColumn: ITankGridColumn = null;
  secondaryColumn: ITankGridColumn = null;

  primaryColumnId: number = -1;
  secondaryColumnId: number = -1;
  primaryColumnSortTypeId: number = this.PRIMARY_SORT_ASCENDING;
  secondaryColumnSortTypeId: number = this.SECONDARY_SORT_ASCENDING;

  currentTankProfile: ITankProfile = null;
  tankGridColumns: ITankGridColumn[] = [];
  private currentTankSub: Subscription;
  tankProfileForm: FormGroup;

  // Local validation state
  validationState: ValidationState = {
    tankProfileName: { isValid: true, isChecking: false },
    isPrivate: { isValid: true, isChecking: false },
  };

  public get disableSave(): boolean {
    return (
      this.tankProfileForm?.disabled ||
      this.tankProfileForm?.pristine ||
      this.hasValidationErrors ||
      this.isAnyValidationChecking
    );
  }

  public get hasValidationErrors(): boolean {
    return this.tankProfileForm?.invalid || !this.validationState.tankProfileName?.isValid;
  }

  public get isAnyValidationChecking(): boolean {
    return this.validationState?.tankProfileName?.isChecking;
  }

  public get readOnly(): boolean {
    return !this.tankProfileForm || this.tankProfileForm.disabled;
  }

  public get isFilteringColumns(): boolean {
    return this.columnFilter.trim().length > 0;
  }

  public get selectedColumnCount(): number {
    return this.tankGridColumns.filter(c => c.required || c.isSelected).length;
  }

  /** TankProfileSettings ctor */
  constructor(
    public translate: TranslateService,
    private logger: LogService,
    private store: Store<AppState>,
    private readonly changeDetectorRefs: ChangeDetectorRef,
    private readonly tankProfileService: TankProfileService,
    private readonly appDialogService: AppDialogService,
    private fb: FormBuilder,
    private readonly settingsDialogService: SettingsDialogService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.initializeModel();
    this.setupValidationListeners();
    this.preTelemetryInfoText = this.getPreTelemetryInfoText();
  }

  ngOnDestroy(): void {
    if (this.currentTankSub) this.currentTankSub.unsubscribe();
    if (this.lastSelectedProfileSub) this.lastSelectedProfileSub.unsubscribe();

    this.destroy$.next(true);
    this.destroy$.complete();
  }

  setupTankProfileSubscription() {
    //current editing tank profile observable
    if (!this.currentTankProfile$) {
      this.logger.error('setupTankProfileSubscription this.currentTankProfile$ is unkown ');
      return;
    }

    this.currentTankSub = this.currentTankProfile$
      .pipe(
        takeUntil(this.destroy$),
        filter(tp => !!tp),
        switchMap(tp => {

          //if in preview mode, do not load the profile again
          //previewing will update the state store
          if(this.inPreview && tp.tankProfileId === this.lastSelectedProfileId) {
            return of(null);
          }
          this.clearForm();
          if (tp && !tp.tankGridColumns) {
            //refresh the tank profile if missing columns
            this.loadingProfile = true;
            this.changeDetectorRefs.detectChanges();
            return this.tankProfileService.getTankProfile(tp.tankProfileId).pipe(
              //a copy, never tp itself: tp is the profile list's own object, and Apply dispatches that same
              //object into NgRx, which freezes it in dev - writing to it then threw and killed this stream
              map(tankProfile => ({ ...tp, tankGridColumns: tankProfile.tankGridColumns })),
              //a failed load must not end the subscription, or the editor stops following the list for good
              catchError(err => {
                this.logger.error('Failed to load tank profile columns', err);
                return of(null);
              }),
              finalize(() => {
                this.loadingProfile = false;
                this.changeDetectorRefs.markForCheck();
              })
            );
          }
          return of(tp);
        })
      )
      .subscribe(tp => {
        if (!tp) return;
        this.formSetup(tp);
      });

      if(!this.lastSelectedProfileSub) {
        this.lastSelectedProfileSub = this.lastSelectedProfile$.pipe(takeUntil(this.destroy$)).subscribe(tp => {
          //null until a profile has been applied
          this.lastSelectedProfileId = tp?.tankProfileId;
        });
      }
  }

  private clearForm() {
    this.inPreview = false;
    this.tankProfileForm?.reset();
    this.tankGridColumns.length = 0;
    this.columnFilter = '';
    this.visibleColumns = [];
    this.lastCheckedName = null;
    this.tankProfileForm?.disable();
  }

  private setupColumns() {
    //reset ids too - a profile without a sort must not inherit the previous profile's
    this.primaryColumnId = this.NO_SORT_COLUMN;
    this.secondaryColumnId = this.NO_SORT_COLUMN;
    this.primaryColumnSortTypeId = this.PRIMARY_SORT_ASCENDING;
    this.secondaryColumnSortTypeId = this.SECONDARY_SORT_ASCENDING;
    this.tankGridColumns.length = 0;
    this.columnLabels.clear();

    this.currentTankProfile.tankGridColumns.forEach(tgc => {
      this.tankGridColumns.push({ ...tgc });
      this.columnLabels.set(tgc.columnID, this.translateColumn(tgc.databaseName));
    });
    this.columnsByLabel = [...this.tankGridColumns].sort((a, b) =>
      this.columnLabel(a).localeCompare(this.columnLabel(b))
    );
    this.onSortSearch('primary', '');
    this.onSortSearch('secondary', '');
    this.applyColumnFilter();

    //setup primary sort
    this.primaryColumn = this.tankGridColumns.find(
      tgc => tgc.userColumnSortTypeID === 2 || tgc.userColumnSortTypeID === 3
    );

    if (this.primaryColumn) {
      this.primaryColumnId = this.primaryColumn.columnID;
      this.primaryColumnSortTypeId = this.primaryColumn.userColumnSortTypeID;
    }

    //setup secondary sort
    this.secondaryColumn = this.tankGridColumns.find(
      tgc => tgc.userColumnSortTypeID === 4 || tgc.userColumnSortTypeID === 5
    );
    if (this.secondaryColumn) {
      this.secondaryColumnId = this.secondaryColumn.columnID;
      this.secondaryColumnSortTypeId = this.secondaryColumn.userColumnSortTypeID;
    }
  }

  markDirty() {
    this.tankProfileForm?.markAsDirty();
    this.changeDetectorRefs.markForCheck();
  }

  private formSetup(tp: ITankProfile) {
    this.currentTankProfile = cloneDeep(tp);

    this.setupColumns();
    this.pageLengthOptions =
      tp.pageLength > 0 && !this.pageLengths.includes(tp.pageLength)
        ? [...this.pageLengths, tp.pageLength].sort((a, b) => a - b)
        : this.pageLengths;

    this.tankProfileForm.enable();

    this.tankProfileForm.patchValue({
      tankProfileName: tp.tankProfileName,
      includeChildOrganization: tp.includeChildOrganization,
      includeAffiliatedOrganization: tp.includeAffiliatedOrganization,
      pageLength: tp.pageLength,
      isPrivate: tp.isPrivate,
      primaryColumnId: this.primaryColumnId,
      primaryColumnSortTypeId: this.primaryColumnSortTypeId,
      secondaryColumnId: this.secondaryColumnId,
      secondaryColumnSortTypeId: this.secondaryColumnSortTypeId,
      // Add other controls as needed
    });

    // Mark form as pristine after patching values
    this.tankProfileForm.markAsPristine();
    this.tankProfileForm.markAsUntouched();
    setTimeout(() => {
      if (this.currentTankProfile.isReadOnly && this.tankProfileForm.enabled) {
        this.tankProfileForm.disable({
          onlySelf: false,
          emitEvent: true,
        });
      } else if (!this.tankProfileForm.enabled && !this.currentTankProfile.isReadOnly) {
        this.tankProfileForm.enable({
          onlySelf: false,
          emitEvent: true,
        });
      }
      this.syncSortControls();
      this.changeDetectorRefs.detectChanges();
    }, 100);
  }

  drop(event: CdkDragDrop<any[]>) {
    //indexes are positions in the rendered list, so this is only correct while the list is unfiltered
    if (this.isFilteringColumns) return;
    moveItemInArray(this.tankGridColumns, event.previousIndex, event.currentIndex);
    this.applyColumnFilter();
    setTimeout(() => {
      this.markDirty();
    }, 0);
  }

  columnLabel(column: ITankGridColumn): string {
    return this.columnLabels.get(column.columnID) ?? column.columnName;
  }

  trackColumn(_: number, column: ITankGridColumn): number {
    return column.columnID;
  }

  onColumnFilterChange(value: string): void {
    this.columnFilter = value ?? '';
    this.applyColumnFilter();
  }

  clearColumnFilter(): void {
    this.onColumnFilterChange('');
  }

  selectAllColumns(): void {
    this.setOptionalColumnsSelected(true);
  }

  clearOptionalColumns(): void {
    this.setOptionalColumnsSelected(false);
  }

  private setOptionalColumnsSelected(selected: boolean): void {
    if (this.readOnly) return;
    this.tankGridColumns.filter(c => !c.required).forEach(c => (c.isSelected = selected));
    this.markDirty();
  }

  onSortSearch(which: 'primary' | 'secondary', term: string): void {
    this.sortSearch[which] = term;
    this.sortOptions[which] = this.matchColumns(this.columnsByLabel, term);
    this.changeDetectorRefs.markForCheck();
  }

  private applyColumnFilter(): void {
    this.visibleColumns = this.matchColumns(this.tankGridColumns, this.columnFilter);
    this.changeDetectorRefs.markForCheck();
  }

  private matchColumns(columns: ITankGridColumn[], term: string): ITankGridColumn[] {
    const needle = (term ?? '').trim().toLowerCase();
    return needle ? columns.filter(c => this.columnLabel(c).toLowerCase().includes(needle)) : [...columns];
  }

  /**
   * A direction only means something once its column is chosen, and the grid ignores a secondary
   * sort when there is no primary (SortService.OrderQuery), so clearing First also clears Then.
   */
  private syncSortControls(): void {
    if (!this.tankProfileForm || this.tankProfileForm.disabled) return;
    const form = this.tankProfileForm;
    const hasPrimary = form.get('primaryColumnId').value > 0;
    if (!hasPrimary) {
      form.get('secondaryColumnId').setValue(this.NO_SORT_COLUMN, { emitEvent: false });
    }
    const hasSecondary = form.get('secondaryColumnId').value > 0;
    this.setControlEnabled('primaryColumnSortTypeId', hasPrimary);
    this.setControlEnabled('secondaryColumnId', hasPrimary);
    this.setControlEnabled('secondaryColumnSortTypeId', hasSecondary);
    this.changeDetectorRefs.markForCheck();
  }

  private setControlEnabled(name: string, enabled: boolean): void {
    const control = this.tankProfileForm.get(name);
    if (enabled && control.disabled) control.enable({ emitEvent: false });
    if (!enabled && control.enabled) control.disable({ emitEvent: false });
  }

  setProfileToModel() {
    const selectedTankGridColumns = this.updateColumnOrderAndSort(this.tankGridColumns);
    const currentTankProfile = cloneDeep(this.currentTankProfile);
    currentTankProfile.tankGridColumns = selectedTankGridColumns;
    
    //primary Column User Sort
    //raw value: a confirmed rename and an emptied sort both leave their controls disabled, and .value drops disabled controls
    const formValue = this.tankProfileForm.getRawValue();
    this.primaryColumnId = formValue.primaryColumnId;
    this.primaryColumnSortTypeId = formValue.primaryColumnSortTypeId;
    this.secondaryColumnId = formValue.secondaryColumnId;
    this.secondaryColumnSortTypeId = formValue.secondaryColumnSortTypeId;
    if (this.primaryColumnId > 0) {
      let i = currentTankProfile.tankGridColumns.findIndex(
        pc => pc.columnID === this.primaryColumnId
      );
      if (i > -1)
        currentTankProfile.tankGridColumns[i].userColumnSortTypeID =
          this.primaryColumnSortTypeId;
    }
    if (this.secondaryColumnId > 0) {
      let i = currentTankProfile.tankGridColumns.findIndex(
        pc => pc.columnID === this.secondaryColumnId
      );
      if (i > -1)
        currentTankProfile.tankGridColumns[i].userColumnSortTypeID =
          this.secondaryColumnSortTypeId;
    }
    currentTankProfile.tankProfileName =
      formValue.tankProfileName ?? currentTankProfile.tankProfileName;
    currentTankProfile.includeChildOrganization = formValue.includeChildOrganization;
    currentTankProfile.includeAffiliatedOrganization = formValue.includeAffiliatedOrganization;
    currentTankProfile.pageLength = formValue.pageLength;
    currentTankProfile.isPrivate = formValue.isPrivate;
    // Add other fields as needed
    this.currentTankProfile = currentTankProfile;
  }

  reset(): void {
    this.tankProfileService
      .getTankProfile(this.currentTankProfile.tankProfileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tp: ITankProfile) => {
        if (tp.tankGridColumns && tp.tankGridColumns.length > 0) {
          this.formSetup(tp);
        } else {
          this.currentTankProfile = null;
        }
      });
  }

  preview(): void {
    this.inPreview = true;
    this.setProfileToModel();
    this.store.dispatch(new TankProfileActions.UpdateCurrentTankProfile(this.currentTankProfile));
    this.settingsDialogService.hide(); // Call the hide method
    this.changeDetectorRefs.detectChanges();
  }

  save(): void {
    this.inPreview = false;
    if (this.hasValidationErrors || this.isAnyValidationChecking) {
      return;
    }
    this.store
      .select((state: AppState) => state.gridFilter?.['includeAllGateways'])
      .pipe(take(1))
      .subscribe(includeAllGateways => {
        if (includeAllGateways) {
          this.appDialogService.showGeneralMessage(
            this.translate.instant('TankFilters.SkytankGatewayNotIncludedInProfile')
          );
          return;
        }
        this.setProfileToModel();
        this.tankProfileService.saveTankProfile(this.currentTankProfile).subscribe({
          next: (result: ITankProfile) => {
            this.tankProfileForm.markAsPristine();
            this.tankProfileForm.markAsUntouched();
            this.store.dispatch(new TankProfileActions.UpdateCurrentTankProfile(this.currentTankProfile));
            this.tankProfileSaved.emit(result);
          },
        });
      });
  }

  updateColumnOrderAndSort(columns: ITankGridColumn[]): ITankGridColumn[] {
    if (!columns) return columns;

    const resortedColumns = cloneDeep(columns);
    resortedColumns?.forEach((c, i) => {
      c.columnSortIndex = i;
      c.userColumnSortTypeID = 0
    });
    return resortedColumns;
  }

  cancel(): void {
    //refresh the data internally by resubscribing
    this.inPreview = false;
    this.currentTankSub.unsubscribe();
    this.initializeModel();
    this.setupTankProfileSubscription();
    this.settingsDialogService.close();
  }

  public resetValidationState() {
    this.tankProfileForm.get('tankProfileName')?.setErrors(null);
  }

  private setupValidationListeners() {
    // Listen to tankProfileName changes
    this.tankProfileForm
      .get('tankProfileName')
      .valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        //don't validate if the form is disabled
        if (this.tankProfileForm.disabled) return;
        this.validateTankProfileName(value);
      });

    this.tankProfileForm
      .get('isPrivate')
      .valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        //don't validate if the form is disabled
        if (this.tankProfileForm.disabled) return;
        this.validateIsPrivate(value);
      });

    merge(
      this.tankProfileForm.get('primaryColumnId').valueChanges,
      this.tankProfileForm.get('secondaryColumnId').valueChanges
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncSortControls());
  }

  public validateTankProfileName(value: string) {
    if (!value || value.trim().length === 0) {
      this.validationState.tankProfileName = { isValid: true, isChecking: false };
      this.lastCheckedName = value;
      return;
    }

    this.validationState.tankProfileName = { isValid: true, isChecking: true };
    this.changeDetectorRefs.markForCheck();

    this.tankProfileService
      .tankProfileNameAlreadyExists(value, this.currentTankProfile?.tankProfileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: exists => {
          this.validationState.tankProfileName = {
            isValid: !exists,
            errorMessage: exists ? 'TankProfile.TankProfileNameAlreadyExists' : undefined,
            isChecking: false,
          };
          this.lastCheckedName = value;
          this.changeDetectorRefs.markForCheck();
        },
        error: err => {
          //never leave isChecking latched - it permanently disables Save
          this.logger.error('Tank profile name validation failed', err);
          this.validationState.tankProfileName = { isValid: true, isChecking: false };
          this.lastCheckedName = value;
          this.changeDetectorRefs.markForCheck();
        },
      });
  }

  public validateIsPrivate(value: boolean) {
    this.validationState.isPrivate = { isValid: true, isChecking: true };
    this.changeDetectorRefs.markForCheck();
    if (!value || value === null || value === undefined) {
      this.validationState.isPrivate = { isValid: true, isChecking: false };
      this.changeDetectorRefs.markForCheck();
      return;
    }

    if (value && this.currentTankProfile) {
      this.tankProfileService
        .tankProfileHasOtherUsersAttached(this.currentTankProfile.tankProfileId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(hasDependencies => {
          this.validationState.isPrivate = {
            isValid: !hasDependencies,
            errorMessage: hasDependencies
              ? 'TankProfile.TankProfileHasOtherUsersAttached'
              : undefined,
            isChecking: false,
          };
        });
    }
  }

  private initializeModel(): void {
    this.tankGridColumns = [];
    this.primaryColumnSortTypeId = 2;
    this.secondaryColumnSortTypeId = 3;
    this.secondaryColumn = null;
    this.primaryColumn = null;
    this.primaryColumnId = -1;
    this.secondaryColumnId = -1;
    this.currentTankProfile = null;
  }

  translateColumn(databaseName: string): string {
    let columnKey: string;
    switch (databaseName) {
      case '[ALARM STATUS ICON]': {
        columnKey = 'VolumeAlarmStatus';
        break;
      }
      case '[TANK ICON]': {
        columnKey = 'TankStatus';
        break;
      }
      case '[EXTRAORDINARY USAGE ICON]': {
        columnKey = 'ExtraordinaryUsageAlarm';
        break;
      }
      case '[COMM STATUS ICON]': {
        columnKey = 'CommStatus';
        break;
      }
      case '[MAINTENANCE ICON]': {
        columnKey = 'MaintStatus';
        break;
      }
      case '[SENSOR STATUS ICON]': {
        columnKey = 'SensorStatus';
        break;
      }
      case '[SENSOR TYPE]': {
        columnKey = 'SensorType';
        break;
      }
      default: {
        columnKey = databaseName;
      }
    }
    return this.translate.instant('Column.' + columnKey);
  }

  getPreTelemetryInfoText(): string {
    const value = this.translate.instant(PRE_TELEMETRY_RESOURCE_KEY);
    return value === PRE_TELEMETRY_RESOURCE_KEY ? this.preTelemetryFallbackText : value;
  }

  showPreTelemetryInfoIcon(databaseName: string): boolean {
    return this.preTelemetryInfoColumns.has((databaseName || '').toLowerCase());
  }

  private initializeForm(): void {
    this.tankProfileForm = this.fb.group({
      tankProfileName: [
        { value: '', disabled: true },
        [Validators.required, Validators.minLength(3), Validators.maxLength(100)],
      ],
      includeChildOrganization: [false],
      includeAffiliatedOrganization: [false],
      pageLength: [50],
      isPrivate: [true],
      primaryColumnId: [this.primaryColumnId],
      primaryColumnSortTypeId: [this.primaryColumnSortTypeId],
      secondaryColumnId: [this.secondaryColumnId],
      secondaryColumnSortTypeId: [this.secondaryColumnSortTypeId],
    });
    // Ensure form starts in pristine state
    this.tankProfileForm.markAsPristine();
    this.tankProfileForm.markAsUntouched();
  }
}
